import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { get } from "api";
import { RewardStatus } from "api/types";
import DiscoverNav from "atoms/icon/icons/discover_nav.svg";
import HomeNav from "atoms/icon/icons/home_nav.svg";
import DiscoverPlus1Nav from "atoms/icon/icons/ic_discover+1_nav_ol.svg";
import WalletPlus1Nav from "atoms/icon/icons/ic_wallet+1_nav_ol.svg";
import RewardsPlus1Nav from "atoms/icon/icons/rewards+1_nav.svg";
import RewardsNav from "atoms/icon/icons/rewards_nav.svg";
import ShopNav from "atoms/icon/icons/shop_nav.svg";
import WalletNav from "atoms/icon/icons/wallet_nav.svg";
import { hasAvailableTreatmentsToSelect } from "pages/root/wallet/utils";

import { Item } from "./item/Item";

/** App navigation menu. */
export const Footer = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  const { data: rewards } = useQuery({
    queryKey: ["/rewards"],
    queryFn: () => get("/rewards", {}),
  });

  const { data: platform } = useQuery({
    queryKey: ["/platform"],
    queryFn: () => get("/platform"),
  });

  const { data: user } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  const promotions = useQuery({
    queryKey: ["/promotions"],
    queryFn: () => get("/promotions"),
    staleTime: 1000 * 60 * 2,
  });

  const userMemberships = useQuery({
    queryKey: ["/platform-user-memberships"],
    queryFn: () => get("/platform-user-memberships"),
    staleTime: 1000 * 60 * 2,
  });

  const { data: treatments } = useQuery({
    queryKey: ["/platform-user-packages"],
    queryFn: () => get("/platform-user-packages"),
    staleTime: 1000 * 60 * 2,
  });

  // Prefetch data for footer tabs on mount
  useEffect(() => {
    // Prefetch Shop page data - this is the most data-heavy tab
    if (platform?.platformSettings?.marketplaceEnabled) {
      queryClient.prefetchQuery(["/shop"], () => get("/shop"));
      queryClient.prefetchQuery(["/listings"], () => get("/listings"), {
        staleTime: 1000 * 60,
      });
      queryClient.prefetchQuery(["/memberships"], () => get("/memberships"));
      queryClient.prefetchQuery(
        ["/shop/discounts"],
        () => get("/shop/discounts"),
        { staleTime: 1000 * 10 }
      );
      queryClient.prefetchQuery(
        ["/shop/custom-categories/listings"],
        () => get("/shop/custom-categories/listings"),
        { staleTime: 1000 * 10 }
      );
      queryClient.prefetchQuery(["shop/categories"], () =>
        get("/shop/categories")
      );
    }

    // Note: Rewards, Promotions, User Memberships, and Packages are already
    // fetched by the useQuery hooks above, so they're already in the cache
  }, [queryClient, platform?.platformSettings]);

  const hasAvailableRewards = rewards?.some(
    (r) => r.status === RewardStatus.Available
  );
  const hasAvailableOffers = promotions?.data?.some(
    (p) => p.expiresOn != null && p.redeemedOn == null
  );
  const hasRedeemedOffers = promotions?.data?.some(
    (p) => p.redeemedOn != null && p.expiresOn != null
  );
  const hasAvailableMembershipTreatmentsToRedeem =
    hasAvailableTreatmentsToSelect(userMemberships?.data || []);

  const walletLink = user?.isGuest ? "/signin" : "/wallet";

  // Determine if the wallet tab should be visible
  const marketplaceEnabled = platform?.platformSettings?.marketplaceEnabled;
  const hasTreatments = (treatments?.length ?? 0) > 0;
  const hasPromotions = promotions?.data?.some((p) => !!p.redeemedOn);
  const hasMemberships = (userMemberships?.data?.length ?? 0) > 0;
  const hasRepeatCashEnabled = platform?.platformSettings?.repeatCashEnabled;

  const walletTabsVisible =
    marketplaceEnabled ||
    hasTreatments ||
    hasPromotions ||
    hasMemberships ||
    hasRepeatCashEnabled;

  // Update indicator position when route changes
  useEffect(() => {
    if (!navRef.current) return;

    const activeLink = navRef.current.querySelector(
      '[data-active="true"]'
    ) as HTMLElement;

    if (activeLink) {
      const containerRect = navRef.current.getBoundingClientRect();
      const linkRect = activeLink.getBoundingClientRect();

      // Add extra padding to make indicator larger than button content
      const extraPadding = 20;

      setIndicatorStyle({
        left: linkRect.left - containerRect.left - extraPadding,
        width: linkRect.width + extraPadding * 2,
      });
    }
  }, [location.pathname]);

  // Run positioning logic on mount
  useEffect(() => {
    if (!navRef.current) return;

    const activeLink = navRef.current.querySelector(
      '[data-active="true"]'
    ) as HTMLElement;

    if (activeLink) {
      const containerRect = navRef.current.getBoundingClientRect();
      const linkRect = activeLink.getBoundingClientRect();

      // Add extra padding to make indicator larger than button content
      const extraPadding = 20;

      setIndicatorStyle({
        left: linkRect.left - containerRect.left - extraPadding,
        width: linkRect.width + extraPadding * 2,
      });
    }
  }, []);

  return (
    <div
      ref={navRef}
      className="relative z-40 flex w-full items-center justify-between bg-white px-[16px] pb-[calc(9px_+_env(safe-area-inset-bottom))] pt-[7px] shadow-footerNavigation"
    >
      {/* Animated background indicator */}
      <div
        className="absolute top-[7px] h-[44px] rounded-full bg-brand-color/10 transition-all duration-300 ease-out"
        style={{
          left: `${indicatorStyle.left}px`,
          width: `${indicatorStyle.width}px`,
        }}
      />

      <Item svg={HomeNav} to="/home" label="Home" />
      {!user?.isGuest && (
        <Item
          svg={hasAvailableOffers ? DiscoverPlus1Nav : DiscoverNav}
          to="/discover"
          label="Discover"
        />
      )}
      {platform?.platformSettings?.marketplaceEnabled && (
        <Item svg={ShopNav} to="/shop" label="Shop" />
      )}
      {platform?.platformSettings?.rewardsEnabled && (
        <Item
          svg={hasAvailableRewards ? RewardsPlus1Nav : RewardsNav}
          to="/rewards"
          label="Rewards"
        />
      )}
      {walletTabsVisible && (
        <Item
          svg={
            hasRedeemedOffers || hasAvailableMembershipTreatmentsToRedeem
              ? WalletPlus1Nav
              : WalletNav
          }
          to={walletLink}
          label="Wallet"
        />
      )}
    </div>
  );
};
